/* eslint-disable no-param-reassign */
import Clarity from '@microsoft/clarity';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enableClarity } from 'infrastructure/serviceConfigs/clarityConfig';
import analyticsService from 'infrastructure/serviceHandlers/Analytics/Analytics';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import { GetUserDetails } from 'services/api/UserApi';
import { TUserResponseType, TUserState } from 'typings/userTypes';
import { setLocalStorageItem } from 'utils/commonFunctions';
import i18next, { getLanguage } from '../../locales/i18n';

export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async (_, thunkAPI) => {
    try {
      const response = await GetUserDetails();
      // console.log('GetUserDetails Response:', response);
      const responseData = response.data;
      if (responseData.id) {
        analyticsService.setUserId(responseData.id);
        analyticsService.setUserProperties({
          Name: `${responseData?.name?.first ?? ''} ${
            responseData?.name?.last ?? ''
          }`.trim(),
          Email: responseData.email,
          Phone: responseData?.Phone_number as unknown as string,
          Identity: responseData.id
        });
        // Re-fetch flags now that the user is identified, so segment-targeted
        // variants resolve for the logged-in user
        analyticsService.fetchExperiment();
      }
      if (enableClarity && responseData && responseData.id)
        Clarity.identify(
          responseData.id,
          '',
          '',
          // `${responseData.name.first} ${responseData.name.last}`
          responseData.id
        );
      if (responseData.language) {
        const userLanguage = getLanguage(responseData.language);
        i18next.changeLanguage(userLanguage);
      }
      setLocalStorageItem('userId', responseData.id as string);
      return responseData;
    } catch (error) {
      console.error('Error in GetUserDetails:', error);
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchUser',
          error_type: 'api_error'
        }
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const initialUserState: TUserState = {
  userDetails: null,
  isLoading: 'idle',
  isAuthenticated: false,
  isEligibleForTrial: false
};

export const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    clearUser: (state) => {
      state.userDetails = null;
      state.isLoading = 'idle';
      state.isAuthenticated = false;
      state.isEligibleForTrial = false;
    }
    // updateFreeTrialEligibility: (state, action) => {
    //   state.isEligibleForTrial = action.payload || false;
    // }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = 'succeeded';
        state.userDetails = action.payload as TUserResponseType;
        state.isAuthenticated = true;
      })
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = 'pending';
      })
      .addCase(fetchUser.rejected, (state) => {
        state.isLoading = 'failed';
        state.isAuthenticated = false;
      });
  }
});

// Action creators are generated for each case reducer function
export const { clearUser } = userSlice.actions;

export default userSlice.reducer;
